import { Home } from "lucide-react";
import type { ApplicantRequirement } from "@/types/contact";

interface Props {
  requirements: ApplicantRequirement[] | null | undefined;
  loading?: boolean;
}

function formatPrice(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(value)) return null;
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

export function ApplicantRequirementSummary({ requirement }: { requirement: ApplicantRequirement }) {
  const dash = "-";

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex rounded-full bg-[#1e293b] px-3 py-1 text-xs font-medium text-white">
          {requirement.profileIntention?.trim() || dash}
        </span>
        <span
          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
            requirement.isActiveProfile
              ? "bg-bridge-accent-tint text-[#009BF5]"
              : "bg-gray-100 text-bridge-muted"
          }`}
        >
          {requirement.isActiveProfile == null ? dash : requirement.isActiveProfile ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="mt-3 text-sm text-bridge-muted">
        External ID:{" "}
        <span className="text-bridge-fg">{requirement.externalId?.trim() || dash}</span>
      </div>
    </>
  );
}

function RequirementCard({ requirement }: { requirement: ApplicantRequirement }) {
  const dash = "-";
  const priceFrom = formatPrice(requirement.priceFrom);
  const priceTo = formatPrice(requirement.priceTo);
  const priceRange =
    priceFrom && priceTo
      ? `${priceFrom} – ${priceTo}`
      : priceFrom
        ? `From ${priceFrom}`
        : priceTo
          ? `Up to ${priceTo}`
          : dash;

  const metaRows: { label: string; value: string }[] = [
    { label: "Qualifier", value: requirement.rentalIntentionPriceQualifier?.trim() || dash },
  ];

  const detailRows: { label: string; value: string }[] = [
    { label: "Price range", value: priceRange },
    { label: "Min beds", value: requirement.bedsMin != null ? String(requirement.bedsMin) : dash },
    { label: "Furnishing", value: requirement.rentalIntentionFurnishing?.trim() || dash },
    { label: "New build", value: requirement.isNewBuild == null ? dash : requirement.isNewBuild ? "Yes" : "No" },
  ];

  return (
    <div className="rounded-md border border-bridge-border/40 bg-gray-50/30 p-4">
      <ApplicantRequirementSummary requirement={requirement} />

      {metaRows.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-bridge-muted">
          {metaRows.map((row) => (
            <span key={row.label}>
              {row.label}: <span className="text-bridge-fg">{row.value}</span>
            </span>
          ))}
        </div>
      ) : null}

      <dl className="mt-3 grid gap-2 border-t border-bridge-border/30 pt-3 sm:grid-cols-2">
        {detailRows.map((row) => (
          <div key={row.label} className="min-w-0">
            <dt className="text-xs text-bridge-muted">{row.label}</dt>
            <dd className="text-sm font-medium text-bridge-fg">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function ApplicantRequirementsTab({ requirements, loading }: Props) {
  if (loading) {
    return (
      <div className="rounded-md border border-bridge-border/40 bg-white p-4 sm:p-6 shadow-sm">
        <div className="h-6 w-48 animate-pulse rounded bg-gray-100" />
        <div className="mt-4 h-24 animate-pulse rounded-md bg-gray-50" />
      </div>
    );
  }

  const list = requirements ?? [];

  return (
    <div className="rounded-md border border-bridge-border/40 bg-white p-4 sm:p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Home className="h-5 w-5 shrink-0 text-bridge-fg" strokeWidth={1.75} aria-hidden />
        <h3 className="text-base font-semibold text-bridge-fg">Applicant Requirements</h3>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-bridge-muted">No applicant requirements on file.</p>
      ) : (
        <div className="space-y-3">
          {list.map((req) => (
            <RequirementCard key={req.id} requirement={req} />
          ))}
        </div>
      )}
    </div>
  );
}
